import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { getDocumentTypesDict } from '@Store/selectors/dictionarySelector';
import {
  DocumentListResponse,
  MaterialRequisitionDocumentResponse,
} from '@Types/api/form';
import { mrSelectAllManagerSelector } from '@Features/MaterialRequest/requests/MaterialRequisition/store/selectors/materialRequestSelector';
import { materialRequestActions } from '@Features/MaterialRequest/requests/MaterialRequisition/store/materialRequestSlice';

import { getDocument, getFileTypeExt } from '@Features/utils/document';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import DocumentItem from './components/DocumentItem';
import { getDocumentFolder } from '../../../../utils/document';

import styles from './document-list.module.css';

interface DocumentListItemsProps {
  listItems: Array<MaterialRequisitionDocumentResponse>;
  onEditDocumentItem: (
    documentItem: MaterialRequisitionDocumentResponse
  ) => void;
  onDeleteItem: (
    document: MaterialRequisitionDocumentResponse,
    documentName: string
  ) => void;
  onDownload: (url: string | undefined, filePath: string) => void;
}

const DocumentListItems: React.FC<DocumentListItemsProps> = ({
  listItems,
  onEditDocumentItem,
  onDeleteItem,
  onDownload,
}) => {
  const requisitionId = listItems[0].reqId;
  const documentTypes = useAppSelector(getDocumentTypesDict);
  const dispatch = useAppDispatch();
  const { isSelected, selectedItems } = useAppSelector(
    mrSelectAllManagerSelector
  );
  const [getFormList] = useFormListMutation();
  const [documentsInFolder, setDocumentsInFolder] =
    useState<DocumentListResponse[]>();

  const handleUnselectItem = useCallback(
    (document: MaterialRequisitionDocumentResponse) => {
      dispatch(materialRequestActions.setMRUnselectItem(document));
    },
    [dispatch]
  );

  useEffect(() => {
    if (requisitionId) {
      const documentFolderPath = getDocumentFolder(requisitionId);

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          setDocumentsInFolder(response);
        });
    }
  }, [requisitionId, listItems]);

  return (
    <div className={styles.container}>
      {listItems.map((document) => {
        const matchedDocument =
          documentsInFolder &&
          getDocument(documentsInFolder, document.filePath);

        return (
          <DocumentItem
            key={`${document.docName}-${document.id}`}
            deleteFlag={Boolean(document.deleteFlag)}
            docDesc={document.docDesc}
            docName={document.docName}
            filePath={document.filePath}
            documentId={matchedDocument?.documentId}
            isConfidential={Boolean(document.isConfidential)}
            procDocTypeOption={
              documentTypes.find(
                (documentType) =>
                  documentType.codeNum === document.procDocTypeId
              )?.codeDesc || 'N/A'
            }
            type={getFileTypeExt(document.filePath)}
            handleEditItem={() => onEditDocumentItem(document)}
            handleDeleteItem={() => onDeleteItem(document, document.docName)}
            handleDownloadItem={onDownload}
            handleUnselectItem={() => handleUnselectItem(document)}
            isSelected={isSelected}
            checkedItem={Boolean(
              selectedItems.find(
                (item) =>
                  (item as MaterialRequisitionDocumentResponse).docName ===
                  document.docName
              )
            )}
          />
        );
      })}
    </div>
  );
};

export default DocumentListItems;
